/**
 * Server-side search + paging state for saas-auth's paged lists (`?q=&page=&size=`, 0-based
 * `page`), kept in the URL (TMM-B6, TMMRSL-0072) through `useUrlState`: the search box is debounced
 * (300 ms) and a new search, or a new page size, goes back to page 0.
 */
import { intParam, stringParam, useUrlState } from '@/components/useUrlState';

export const PAGE_SIZES: readonly number[] = [10, 25, 50];

const SCHEMA = {
  q: stringParam(),
  page: intParam(),
  size: intParam(25, PAGE_SIZES),
};

export interface PagedSearch {
  search: string;
  setSearch: (value: string) => void;
  /** The debounced, trimmed query sent to the server. */
  q: string;
  page: number;
  setPage: (page: number) => void;
  size: number;
  setSize: (size: number) => void;
  /** Clears the search at once (an empty list's Clear filters), back to page 0. */
  clear: () => void;
}

export function usePagedSearch(): PagedSearch {
  const url = useUrlState(SCHEMA, { page: 'page', search: 'q' });
  const { q, page, size } = url.values;
  return {
    search: url.searchText,
    setSearch: url.setSearchText,
    q,
    page,
    setPage: (next) => url.set({ page: next }),
    size,
    setSize: (next) => url.set({ size: next }),
    clear: () => {
      url.setSearchText('');
      url.set({ q: '' });
    },
  };
}
